import type { Monitor } from './types';

/** Runs against the loaded client chunk's `Monitor`. */
export type MonitorTask = (monitor: Monitor) => void;
type LoadState = 'idle' | 'loading' | 'ready' | 'failed';

/** Tasks recorded before the client loads, replayed in order; oldest dropped past the limit. */
const QUEUE_LIMIT = 100;

let state: LoadState = 'idle';
let monitor: Monitor | undefined;
let queue: MonitorTask[] = [];

function onClientLoaded(loaded: Monitor) {
  queue.forEach((run) => {
    try {
      run(loaded);
    } catch (e) {
      // one bad entry must not drop the rest
    }
  });
  queue = [];
  monitor = loaded;
  state = 'ready';
}

function onClientLoadFailed() {
  state = 'failed';
  queue = [];
}

function loadClient() {
  try {
    import(/* webpackChunkName: "scdk-monitor" */ './client')
      .then(({ createMonitor }) => onClientLoaded(createMonitor()))
      .catch(onClientLoadFailed);
  } catch (e) {
    onClientLoadFailed();
  }
}

/** Starts loading the client chunk; `import()` is async, so the fetch never blocks the caller. */
export function startClient() {
  if (state !== 'idle') return;
  state = 'loading';
  loadClient();
}

/** Runs `run` now when the client is loaded, else queues it (also before `startClient`); dropped once loading failed. */
export function dispatch(run: MonitorTask) {
  if (monitor) {
    run(monitor);
    return;
  }
  if (state === 'failed') return;
  queue.push(run);
  if (queue.length > QUEUE_LIMIT) queue.shift();
}
